import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Button, Stack, Grid, TextField,
  Radio, RadioGroup, FormControlLabel, FormLabel, Divider, Chip, Alert,
  Card, CardContent, Table, TableBody, TableRow, TableCell, CircularProgress,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTaxAutofill, useCalculateTax } from '../../hooks/useTaxCalculator';
import {
  BLANK_INPUTS, INCOME_FIELDS, DEDUCTION_FIELDS, ASSESSMENT_YEARS, formatINR,
} from './constants';

const TAB_LABELS = ['BASIC INFO', 'INCOME', 'DEDUCTION', 'SUMMARY'];

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function MoneyInput({ label, value, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>{label}</Typography>
      <TextField
        fullWidth
        variant="standard"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
        inputProps={{ min: 0 }}
      />
    </Box>
  );
}

export default function TaxCalculatorForm({ employeeNo, onClear, employeeName }) {
  const { data: autofill, isLoading: autofillLoading } = useTaxAutofill(employeeNo);
  const calc = useCalculateTax();

  const [tab, setTab] = useState(0);
  const [inputs, setInputs] = useState(BLANK_INPUTS);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate inputs from autofill once when it arrives
  useEffect(() => {
    if (autofill && !hydrated) {
      setInputs((prev) => ({
        ...prev,
        annual_salary: Number(autofill.annual_salary || 0),
        annual_basic_da: Number(autofill.annual_basic_da || 0),
        employer_nps_80ccd_2: Number(autofill.employer_nps_80ccd_2 || 0),
        age: autofill.age || '',
      }));
      setHydrated(true);
    }
  }, [autofill, hydrated]);

  const setField = (key) => (val) =>
    setInputs((prev) => ({ ...prev, [key]: val }));

  const handleClear = () => {
    setInputs({ ...BLANK_INPUTS });
    setTab(0);
    calc.reset();
    setHydrated(false); // allow re-hydration if employee/autofill is still in scope
    if (onClear) onClear();
  };

  const handleCalculate = () => {
    calc.mutate({
      ay: inputs.assessment_year,
      inputs: {
        ...inputs,
        age: inputs.age ? Number(inputs.age) : 0,
      },
    });
  };

  // Auto-recalculate when user lands on Summary tab with data present.
  useEffect(() => {
    if (tab === 3) handleCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const headerYear = ASSESSMENT_YEARS.find((y) => y.value === inputs.assessment_year)?.label || '';

  return (
    <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
      <Box sx={{ py: 1.75, px: 3, bgcolor: '#f1f5fb', textAlign: 'center', position: 'relative' }}>
        <Typography variant="h6" sx={{ color: '#0d47a1', fontWeight: 700, letterSpacing: '0.5px', fontSize: { xs: '1rem', md: '1.1rem' } }}>
          CALCULATE TAXES FOR FY{inputs.assessment_year === '2027-28' ? '26-27' : '25-26'}
        </Typography>
        <Box sx={{ width: 40, height: 3, bgcolor: '#ff9800', mx: 'auto', mt: 0.75 }} />
        {employeeName && (
          <Chip
            label={`${employeeName}${employeeNo ? ` (${employeeNo})` : ''}`}
            size="small"
            sx={{ mt: 1.5, bgcolor: '#fff' }}
          />
        )}
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleClear}
          sx={{ position: 'absolute', top: 10, right: 12 }}
        >
          Clear
        </Button>
      </Box>

      {autofillLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      <Box sx={{ px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          {TAB_LABELS.map((label, idx) => (
            <Tab key={label} label={label} value={idx} />
          ))}
        </Tabs>

        {/* BASIC INFO */}
        <TabPanel value={tab} index={0}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Your Age</Typography>
              <TextField
                fullWidth
                variant="standard"
                placeholder="AGE (IN YEARS)"
                type="number"
                value={inputs.age}
                onChange={(e) => setField('age')(e.target.value)}
                inputProps={{ min: 0, max: 120 }}
              />
            </Box>
            <Box>
              <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Residential Status</FormLabel>
              <RadioGroup
                row
                value={inputs.residential_city_type}
                onChange={(e) => setField('residential_city_type')(e.target.value)}
              >
                <FormControlLabel value="metro" control={<Radio />} label="Metro City" />
                <FormControlLabel value="non_metro" control={<Radio />} label="Non - Metro City" />
              </RadioGroup>
            </Box>
            <Divider />
            <Box>
              <FormLabel sx={{ fontWeight: 600, color: 'text.primary' }}>Assessment Year</FormLabel>
              <RadioGroup
                row
                value={inputs.assessment_year}
                onChange={(e) => setField('assessment_year')(e.target.value)}
              >
                {ASSESSMENT_YEARS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{opt.label}</span>
                        {opt.isNew && <Chip label="New" size="small" color="success" />}
                      </Stack>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>
            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
              <Button variant="outlined" onClick={() => setTab(1)}>Next</Button>
            </Stack>
          </Stack>
        </TabPanel>

        {/* INCOME */}
        <TabPanel value={tab} index={1}>
          <Grid container spacing={2}>
            {INCOME_FIELDS.map((f) => (
              <Grid item xs={12} key={f.key}>
                <MoneyInput
                  label={f.label}
                  value={inputs[f.key]}
                  onChange={setField(f.key)}
                />
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ pt: 2 }}>
            <Button variant="outlined" onClick={() => setTab(0)}>Back</Button>
            <Button variant="outlined" onClick={() => setTab(2)}>Next</Button>
          </Stack>
        </TabPanel>

        {/* DEDUCTION */}
        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            {DEDUCTION_FIELDS.map((f) => (
              <Grid item xs={12} key={f.key}>
                <MoneyInput
                  label={f.label}
                  value={inputs[f.key]}
                  onChange={setField(f.key)}
                />
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ pt: 2 }}>
            <Button variant="outlined" onClick={() => setTab(1)}>Back</Button>
            <Button variant="outlined" onClick={() => setTab(3)}>Next</Button>
          </Stack>
        </TabPanel>

        {/* SUMMARY */}
        <TabPanel value={tab} index={3}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tax Summary Based on the Input: ({headerYear})
          </Typography>

          {calc.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {calc.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to calculate. Please retry.
            </Alert>
          )}

          {calc.data && (
            <SummaryView data={calc.data} />
          )}

          <Stack direction="row" justifyContent="center" spacing={2} sx={{ pt: 2 }}>
            <Button variant="outlined" onClick={() => setTab(2)}>Back</Button>
            <Button variant="contained" onClick={handleCalculate}>Recalculate</Button>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 3, fontStyle: 'italic' }}
          >
            Note: This calculator provides an indicative estimate and should not be considered tax
            advice. Please consult your tax advisor before making any decisions. The company does
            not guarantee the accuracy, adequacy, or completeness of the information and is not
            liable for any errors, omissions, or outcomes resulting from its use.
          </Typography>
        </TabPanel>
      </Box>
    </Paper>
  );
}

function SummaryView({ data }) {
  const { old_regime: oldR, new_regime: newR, recommended_regime, savings } = data;

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Card
            variant="outlined"
            sx={{ borderColor: recommended_regime === 'old' ? 'success.main' : 'divider' }}
          >
            <CardContent>
              <Typography variant="caption" color="text.secondary">OLD TAX REGIME:</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatINR(oldR.tax_due)}
              </Typography>
              {recommended_regime === 'old' && (
                <Chip label="Recommended" size="small" color="success" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card
            variant="outlined"
            sx={{ borderColor: recommended_regime === 'new' ? 'success.main' : 'divider' }}
          >
            <CardContent>
              <Typography variant="caption" color="text.secondary">NEW TAX REGIME:</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatINR(newR.tax_due)}
              </Typography>
              {recommended_regime === 'new' && (
                <Chip label="Recommended" size="small" color="success" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {Number(savings) > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          You save <strong>{formatINR(savings)}</strong> by choosing the{' '}
          <strong>{recommended_regime === 'new' ? 'New' : 'Old'}</strong> regime.
        </Alert>
      )}

      <Table size="small">
        <TableBody>
          <TableRow sx={{ '& th': { fontWeight: 600 } }}>
            <TableCell>COMPONENTS</TableCell>
            <TableCell align="right">OLD TAX REGIME</TableCell>
            <TableCell align="right">NEW TAX REGIME</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Total Income</TableCell>
            <TableCell align="right">{formatINR(oldR.total_income)}</TableCell>
            <TableCell align="right">{formatINR(newR.total_income)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Exemptions &amp; Deductions</TableCell>
            <TableCell align="right">{formatINR(oldR.exemptions_deductions)}</TableCell>
            <TableCell align="right">{formatINR(newR.exemptions_deductions)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Taxable Income</TableCell>
            <TableCell align="right">{formatINR(oldR.taxable_income)}</TableCell>
            <TableCell align="right">{formatINR(newR.taxable_income)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Surcharge</TableCell>
            <TableCell align="right">{formatINR(oldR.surcharge)}</TableCell>
            <TableCell align="right">{formatINR(newR.surcharge)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Health &amp; Education Cess</TableCell>
            <TableCell align="right">{formatINR(oldR.cess)}</TableCell>
            <TableCell align="right">{formatINR(newR.cess)}</TableCell>
          </TableRow>
          <TableRow sx={{ '& td': { fontWeight: 700 } }}>
            <TableCell>Tax Due</TableCell>
            <TableCell align="right">{formatINR(oldR.tax_due)}</TableCell>
            <TableCell align="right">{formatINR(newR.tax_due)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
}
